import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { firebase } from '@/shared/firebase';

/**
 * Same key name as nexvia-web-app's FirebaseContext.tsx. The ID token lives here, read directly
 * as a Bearer token on every request to nexvia-api-web — no separate "exchange" call, no cookie.
 * `GET/PATCH /storage/me` (Milestone 2) is the first thing that will actually read this key.
 */
export const TOKEN_STORAGE_KEY = 'serviceToken';

export interface StorageAuthUser {
  uid: string;
  email: string | null;
  /**
   * NEX-1164 — nexvia-api-web refuses a NEW login whose email is not verified (`auth.middleware.ts`,
   * 401 `AUTH_EMAIL_NOT_VERIFIED`): an email + password sign-up stays on `/registro`'s "verificá
   * tu correo" step until this is true. Google logins arrive verified.
   */
  emailVerified: boolean;
}

export interface GoogleSignInUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

interface AuthContextValue {
  user: StorageAuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  authError: string | null;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<GoogleSignInUser>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  refreshEmailVerification: () => Promise<boolean>;
  resendVerificationEmail: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Firebase's compat SDK always rejects with `{ code, message }`, never a generic Error.
 * Matched by shape rather than by an exhaustive list of codes: every Firebase error is
 * `auth/<something>`, and once the credential-specific cases below are handled, any other
 * `auth/*` code is the SDK failing before a request was ever made (bad API key, unauthorized
 * domain, …) — a frontend configuration fault whatever its exact name, not something retrying
 * fixes. Same reasoning as nexvia-backoffice's useAuth.tsx.
 */
/**
 * [ASSUMED] the compat SDK always reports `emailVerified` as a boolean; anything else (a test double
 * without it) counts as verified, so only an explicit `false` holds a session at the verification step.
 */
function toStorageAuthUser(firebaseUser: { uid: string; email: string | null; emailVerified?: boolean }): StorageAuthUser {
  return { uid: firebaseUser.uid, email: firebaseUser.email, emailVerified: firebaseUser.emailVerified !== false };
}

/** Where the verification email's "continue" link brings the person back: the registration form. */
function verificationActionSettings() {
  return { url: `${window.location.origin}/registro` };
}

function describeAuthError(err: unknown): string {
  const code = (err as { code?: string } | null | undefined)?.code ?? '';

  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return 'Correo o contraseña incorrectos. Revisá los datos e intentá de nuevo.';
  }
  if (code === 'auth/invalid-email') {
    return 'Ese correo no es válido.';
  }
  if (code === 'auth/user-disabled') {
    return 'Esta cuenta está deshabilitada. Contactá a Nexvia para más información.';
  }
  // NEX-1164 — the email + password sign-up's own answers.
  if (code === 'auth/email-already-in-use') {
    return 'Ya existe una cuenta con ese correo. Iniciá sesión o recuperá tu contraseña.';
  }
  if (code === 'auth/weak-password') {
    return 'La contraseña tiene que tener al menos 6 caracteres.';
  }
  if (code === 'auth/too-many-requests') {
    return 'Probaste varias veces seguidas. Esperá un rato e intentá de nuevo.';
  }
  // Storage responsive pass (2026-10-27): the Google popup's own failures, mostly on iPhone. They are the
  // person's browser, not a broken setup, so they must not reach the "avisá a IT" message below.
  if (code === 'auth/popup-blocked') {
    return 'Tu navegador bloqueó la ventana de Google. Permití las ventanas emergentes para este sitio y volvé a intentar.';
  }
  if (code === 'auth/network-request-failed') {
    return 'No pudimos conectar con Google. Revisá tu conexión y volvé a intentar.';
  }
  if (code === 'auth/web-storage-unsupported' || code === 'auth/operation-not-supported-in-this-environment') {
    return 'Tu navegador no permite iniciar sesión en este modo (navegación privada o el navegador de otra app). Abrí este sitio en Safari o Chrome.';
  }
  if (code.startsWith('auth/')) {
    return 'La configuración de Firebase de este entorno es inválida. No es un error tuyo — avisá a IT.';
  }
  return 'No pudimos iniciar tu sesión. Intentá de nuevo en un momento.';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<StorageAuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // onIdTokenChanged (not onAuthStateChanged): it also fires when Firebase silently refreshes
    // the token, which is when the stored copy needs to be replaced too.
    const unsubscribe = firebase.auth().onIdTokenChanged(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const token = await firebaseUser.getIdToken();
          localStorage.setItem(TOKEN_STORAGE_KEY, token);
        } catch {
          /* localStorage can be unavailable (private mode, quota) — the session is still valid */
        }
        setUser(toStorageAuthUser(firebaseUser));
      } else {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    setAuthError(null);
    try {
      await firebase.auth().signInWithEmailAndPassword(email.trim(), password);
    } catch (err) {
      setAuthError(describeAuthError(err));
      throw err;
    }
  };

  /**
   * Google sign-in — same Firebase mechanism `signInWithEmail` already uses (no new auth path on
   * the backend), just a different Firebase provider, mirroring nexvia-backoffice's useAuth.tsx
   * (`GoogleAuthProvider` + `signInWithPopup`). This function only ever talks to Firebase, same
   * scope as `signInWithEmail` above.
   *
   * NEX-1164 (2026-10-16): self-registration is decided (business doc §5). A brand-new Google
   * identity is NOT turned into an account here or in Login.tsx any more: Login asks
   * `GET /storage/me` and sends a login with no depósito account to `/registro`
   * (`src/pages/RegistroPage.tsx`), which creates it with the real RUT and razón social.
   */
  const signInWithGoogle = async (): Promise<GoogleSignInUser> => {
    setAuthError(null);
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await firebase.auth().signInWithPopup(provider);
      const firebaseUser = result.user;
      if (!firebaseUser) {
        throw { code: 'auth/null-user' };
      }
      return { uid: firebaseUser.uid, email: firebaseUser.email, displayName: firebaseUser.displayName };
    } catch (err) {
      const code = (err as { code?: string } | null | undefined)?.code ?? '';
      const isPopupCancelled = code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
      if (!isPopupCancelled) {
        setAuthError(describeAuthError(err));
      }
      throw err;
    }
  };

  /**
   * NEX-1164 — email + password sign-up (business doc §4: the depósito has its own web and login),
   * same Firebase call as nexvia-web-app's `firebaseRegister` (`createUserWithEmailAndPassword`).
   * Unlike the web app, the panel sends the verification email right away: nexvia-api-web only
   * accepts a new login once its email is verified (`AUTH_EMAIL_NOT_VERIFIED`), and `/registro`
   * waits on it before `POST /auth/register-storage`. A failed verification email does not undo
   * the account: `/registro` offers to send it again.
   */
  const signUpWithEmail = async (email: string, password: string) => {
    setAuthError(null);
    let created;
    try {
      created = await firebase.auth().createUserWithEmailAndPassword(email.trim(), password);
    } catch (err) {
      setAuthError(describeAuthError(err));
      throw err;
    }
    try {
      await created.user?.sendEmailVerification(verificationActionSettings());
    } catch {
      /* the verification step offers "Reenviar el correo" */
    }
  };

  /**
   * NEX-1164 — re-reads the signed-in user after the person clicked the verification link: `reload()`
   * refreshes `emailVerified`, and a forced token refresh makes the next request carry
   * `email_verified: true` (the API reads it off the ID token, not off Firebase). Returns the new value.
   */
  const refreshEmailVerification = async (): Promise<boolean> => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) return false;
    await currentUser.reload();
    const refreshed = firebase.auth().currentUser ?? currentUser;
    if (refreshed.emailVerified) {
      const token = await refreshed.getIdToken(true);
      try {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
      } catch {
        /* localStorage can be unavailable (private mode, quota) — the session is still valid */
      }
    }
    setUser(toStorageAuthUser(refreshed));
    return refreshed.emailVerified !== false;
  };

  const resendVerificationEmail = async () => {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) return;
    await currentUser.sendEmailVerification(verificationActionSettings());
  };

  const sendPasswordReset = async (email: string) => {
    await firebase.auth().sendPasswordResetEmail(email.trim());
  };

  const logout = async () => {
    await firebase.auth().signOut();
    setUser(null);
    setAuthError(null);
    navigate('/login');
  };

  const clearAuthError = () => setAuthError(null);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        authError,
        signInWithEmail,
        signInWithGoogle,
        signUpWithEmail,
        refreshEmailVerification,
        resendVerificationEmail,
        sendPasswordReset,
        logout,
        clearAuthError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
